import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/components/ui/button";
import { useToast } from "@/shared/hooks/use-toast";
import { Spinner } from "@/shared/components/ui/spinner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { useWorkspace } from "@/features/Workspaces/state/WorkspaceContext";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Label } from "@/shared/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { X } from "lucide-react";
import { ProfileService } from "@/features/Profile/services/profileService";
import { WorkspaceUsersService } from "@/features/Workspaces/services/workspaceUsersService";
import { OrganizationService } from "@/features/Organization/services/organizationService";
import { UserService } from "@/features/Users/services/userService";
import { UserRole } from "@/shared/types/user-role";
import { useIsSuperAdminRole } from "@/shared/hooks/use-system-role";
import { useSuperAdminOrg } from "@/shared/contexts/SuperAdminOrgContext";

interface Workspace {
  id: string;
  name: string;
  description?: string;
  logo_url?: string;
}

interface User {
  id: string;
  full_name: string | null;
  email: string;
  avatar_url: string | null;
  status: string;
  role?: UserRole;
  products?: Workspace[];
}

interface EditUserWorkspacesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
  organizationProducts: Workspace[];
}

export const EditUserWorkspacesDialog = ({ open, onOpenChange, user, organizationProducts = [] }: EditUserWorkspacesDialogProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const { isSuperAdmin: currentUserIsSuperAdmin } = useIsSuperAdminRole();
  const { selectedOrganization } = useSuperAdminOrg();
  const { reloadUserProducts } = useWorkspace();
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('products');
  const [selectedRole, setSelectedRole] = useState<UserRole>(UserRole.USER);

  // Estado para armazenar o ID da organização
  const [organizationIdFromService, setOrganizationIdFromService] = useState<string | undefined>(undefined);

  // Superadmins: usar org selecionada; demais: buscar do perfil
  useEffect(() => {
    if (currentUserIsSuperAdmin && selectedOrganization?.id) {
      setOrganizationIdFromService(selectedOrganization.id);
      return;
    }
    const fetchOrganizationId = async () => {
      try {
        const userProfile = await OrganizationService.getCurrentUserProfile();
        if (userProfile && userProfile.organization_id) {
          setOrganizationIdFromService(userProfile.organization_id);
        }
      } catch (error) {
        console.error('[EditUserWorkspacesDialog] Erro ao obter o perfil do usuário:', error);
      }
    };

    fetchOrganizationId();
    // Intentional: resolve the organization id only once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Manipulador para alteração de abas
  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };


  useEffect(() => {
    if (user) {
      setSelectedRole(user.role || UserRole.USER);
    }
  }, [user]);

  // Consulta para buscar os vínculos entre produtos e o usuário selecionado
  // Usado apenas para preencher a seleção inicial de produtos
  const { isLoading: isLoadingLinks } = useQuery({
    queryKey: ['user-workspace-links', user?.id, organizationIdFromService, open],
    queryFn: async () => {
      if (!user?.id) return [];
      try {
        // Usar o UserService que possui o método getUserProducts para garantir a autenticação JWT
        const products = await UserService.getUserProducts(user.id);

        // Garantir que temos um array de produtos válido
        const processedData = Array.isArray(products) ? products : [];

        // Extrair os IDs dos produtos e atualizar o estado
        if (processedData.length > 0) {
          // Usando a tipagem adequada para processar os produtos
          let productIds: string[] = [];

          // Extrair os IDs com verificação segura da estrutura dos objetos
               // eslint-disable-next-line @typescript-eslint/no-explicit-any
          productIds = processedData.map((item: any) => {
            // Verificar qual propriedade contém o ID do produto
            if (item && typeof item === 'object') {
              if ('product_id' in item && item.product_id) {
                return item.product_id;
              } else if ('id' in item && item.id) {
                return item.id;
              }
            }
            return null;
          }).filter(Boolean) as string[]; // Remover itens nulos e converter para string[]

          setSelectedProductIds(productIds);
        } else {
          setSelectedProductIds([]);
        }

        return processedData;
      } catch (error) {
        console.error('Erro ao buscar vínculos produto-usuário:', error);
        setSelectedProductIds([]);
        return [];
      }
    },
    enabled: !!user?.id && open
  });

  // Mutação para atualizar todos os produtos do usuário
  const updateUserProductsMutation = useMutation({
    mutationFn: async ({ userId, productIds }: { userId: string; productIds: string[] }) => {
      try {
        const response = await WorkspaceUsersService.updateUserProducts(userId, {
          product_ids: productIds,
        });
        return response;
      } catch (error) {
        console.error('Erro ao atualizar produtos do usuário:', error);
        throw error;
      }
    }
  });

  // Mutação para atualizar a role do usuário
  const updateRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: UserRole }) => {
      try {
        // Usar o ProfileService para atualizar o perfil com a nova role
        const response = await ProfileService.update(userId, { role });
        return response;
      } catch (error) {
        console.error('Erro ao atualizar role do usuário:', error);
        throw error;
      }
    }
  });

  const handleToggleProduct = (workspaceId: string) => {
    setSelectedProductIds(prev =>
      prev.includes(workspaceId)
        ? prev.filter(id => id !== workspaceId)
        : [...prev, workspaceId]
    );
  };

  const handleSave = async () => {
    if (!user) return;

    setIsProcessing(true);

    try {
      // Verificar se estamos apenas na aba de funções
      // Superadmins are forced to 'role' tab, but activeTab state may not reflect it
      const effectiveTab = selectedRole === UserRole.SUPERADMIN ? 'role' : activeTab;
      if (effectiveTab === 'role') {
        if (user.role !== selectedRole) {
          await updateRoleMutation.mutateAsync({ userId: user.id, role: selectedRole });

          queryClient.invalidateQueries({
            predicate: (query) => {
              const queryKey = Array.isArray(query.queryKey) ? query.queryKey[0] : query.queryKey;
              return queryKey === 'organization-users';
            }
          });

          toast({
            title: t("settings.users.updateWorkspacesSuccess"),
            description: t("settings.users.updateWorkspacesSuccessDescription")
          });

          onOpenChange(false);
          setIsProcessing(false);
          return;
        } else {
          // Nenhuma alteração foi feita
          toast({
            title: t("common.noChanges"),
            description: t("common.noChangesDescription")
          });

          onOpenChange(false);
          setIsProcessing(false);
          return;
        }
      }

      // Se estamos na aba de produtos, utilizar o novo endpoint para atualizar todos de uma vez

      // Filtrar IDs inválidos (undefined, null, strings vazias)
      const validProductIds = selectedProductIds.filter(id => id && typeof id === 'string' && id.trim() !== '');

      const result = await updateUserProductsMutation.mutateAsync({
        userId: user.id,
        productIds: validProductIds
      });

      // Invalidar queries relacionadas a produtos do usuário
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = Array.isArray(query.queryKey) ? query.queryKey[0] : query.queryKey;
          // Invalidar queries genéricas
          if (
            queryKey === 'user-workspace-links' ||
            queryKey === 'organization-users' ||
            queryKey === 'user-products' ||
            queryKey.includes('products-users') ||
            queryKey.includes('users')
          ) {
            return true;
          }

          // Invalidar query específica do usuário que foi modificado
          if (Array.isArray(query.queryKey) &&
            query.queryKey.length === 2 &&
            query.queryKey[0] === 'user-products' &&
            query.queryKey[1] === user.id) {
            return true;
          }

          return false;
        }
      });

      // Forçar refetch imediato de queries importantes
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) && query.queryKey[0] === 'organization-users'
      });

      // Força o refetch específico dos produtos deste usuário
      queryClient.refetchQueries({
        queryKey: ['user-products', user.id]
      });

      // Se o usuário atual estiver sendo editado, recarregar produtos na navbar
      if (user.id === currentUser?.id) {
        await reloadUserProducts();
      }

      toast({
        title: t("settings.users.updateWorkspacesSuccess"),
        description: t("settings.users.updateWorkspacesSuccessDescription", {
          added: result.added,
          removed: result.removed,
          total: result.total
        })
      });

      onOpenChange(false);
    } catch (error) {
      console.error('Erro ao atualizar produtos do usuário:', error);
      toast({
        variant: "destructive",
        title: t("settings.users.updateWorkspacesError"),
        description: t("settings.users.updateWorkspacesErrorDescription")
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center ${open ? 'flex' : 'hidden'} bg-black/50`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      {/* Dialog box with fixed size */}
      <div className="relative bg-background rounded-lg shadow-lg w-[500px] min-h-[450px] max-h-[85vh] overflow-hidden flex flex-col border">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold">{t("settings.users.editUserSettings")}</h2>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-sm"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </Button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto">
          {/* Card de informações do usuário */}
          {user && (
            <div className="flex items-center p-3 mb-6 bg-muted/30 dark:bg-muted/10 rounded-lg border">
              <div className="flex-shrink-0 h-14 w-14 rounded-full overflow-hidden bg-muted mr-3">
                {user.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt={user.full_name || user.email}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full w-full bg-primary/10 text-lg font-medium">
                    {(user.full_name || user.email).charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                {user.full_name && (
                  <h3 className="text-base font-medium">{user.full_name}</h3>
                )}
                <p className="text-sm text-muted-foreground">{user.email}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t(`settings.users.roles.${user.role || UserRole.USER}`)}
                </p>
              </div>
            </div>
          )}

          <Tabs value={selectedRole === UserRole.SUPERADMIN ? 'role' : activeTab} onValueChange={handleTabChange}>
            {selectedRole !== UserRole.SUPERADMIN ? (
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="products">
                  {t("settings.users.tabsWorkspaces")}
                </TabsTrigger>
                <TabsTrigger value="role">
                  {t("settings.users.tabsRole")}
                </TabsTrigger>
              </TabsList>
            ) : (
              <TabsList className="grid w-full grid-cols-1 mb-4">
                <TabsTrigger value="role">
                  {t("settings.users.tabsRole")}
                </TabsTrigger>
              </TabsList>
            )}
            <TabsContent value="products">
              <div className="py-4">
                {isLoadingLinks ? (
                  <div className="flex justify-center p-4">
                    <Spinner className="h-6 w-6" />
                  </div>
                ) : organizationProducts.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center">
                    {t("settings.users.noWorkspacesInOrganization")}
                  </p>
                ) : (
                  <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2">
                    {organizationProducts.map(workspace => (
                      <div key={workspace.id} className="flex items-center space-x-3 rounded-lg border p-3 hover:bg-muted/50 dark:hover:bg-muted/20 transition-colors">
                        <div className="flex-shrink-0 h-10 w-10 rounded-md overflow-hidden bg-muted">
                          {workspace.logo_url ? (
                            <img src={workspace.logo_url} alt={workspace.name} className="h-full w-full object-cover" />
                          ) : (
                            <div className="flex items-center justify-center h-full w-full bg-primary/10 dark:bg-primary/20">
                              {workspace.name.charAt(0)}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <Label
                            htmlFor={`workspace-${workspace.id}`}
                            className="text-sm font-medium cursor-pointer"
                          >
                            {workspace.name}
                          </Label>
                          {workspace.description && (
                            <p className="text-xs text-muted-foreground truncate">
                              {workspace.description}
                            </p>
                          )}
                        </div>
                        <Checkbox
                          id={`workspace-${workspace.id}`}
                          checked={selectedProductIds.includes(workspace.id)}
                          onCheckedChange={() => handleToggleProduct(workspace.id)}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
            <TabsContent value="role">
              <div className="py-4">
                <div className="space-y-4">
                  {currentUserIsSuperAdmin && (
                    <div className="flex items-center space-x-3 rounded-lg border border-amber-300 dark:border-amber-700 p-3 bg-amber-50/50 dark:bg-amber-950/20">
                      <div className="flex-shrink-0 h-10 w-10 rounded-md overflow-hidden bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6 text-amber-600 dark:text-amber-400"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <Label
                          htmlFor="role-superadmin"
                          className="text-sm font-medium cursor-pointer"
                        >
                          {t("settings.users.roleSuperAdmin", "Super Admin")}
                        </Label>
                        <p className="text-xs text-muted-foreground mt-0.5">{t("settings.users.roleSuperAdminDesc", "Acesso total a todas as organizações e produtos")}</p>
                      </div>
                      <Checkbox
                        id="role-superadmin"
                        checked={selectedRole === UserRole.SUPERADMIN}
                        onCheckedChange={() => setSelectedRole(UserRole.SUPERADMIN)}
                      />
                    </div>
                  )}
                  <div className="flex items-center space-x-3 rounded-lg border p-3">
                    <div className="flex-shrink-0 h-10 w-10 rounded-md overflow-hidden bg-muted">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6 m-auto"><path d="M12 12a5 5 0 0 1 5 5"></path><path d="M12 12a5 5 0 0 1-5 5"></path></svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <Label
                        htmlFor="role-admin"
                        className="text-sm font-medium cursor-pointer"
                      >
                        {t("settings.users.roleAdmin")}
                      </Label>
                    </div>
                    <Checkbox
                      id="role-admin"
                      checked={selectedRole === UserRole.ADMIN}
                      onCheckedChange={() => setSelectedRole(UserRole.ADMIN)}
                    />
                  </div>
                  <div className="flex items-center space-x-3 rounded-lg border p-3">
                    <div className="flex-shrink-0 h-10 w-10 rounded-md overflow-hidden bg-muted">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6 m-auto"><path d="M12 12a5 5 0 0 1 5 5"></path><path d="M12 12a5 5 0 0 1-5 5"></path></svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <Label
                        htmlFor="role-user"
                        className="text-sm font-medium cursor-pointer"
                      >
                        {t("settings.users.roleUser")}
                      </Label>
                    </div>
                    <Checkbox
                      id="role-user"
                      checked={selectedRole === UserRole.USER}
                      onCheckedChange={() => setSelectedRole(UserRole.USER)}
                    />
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <div className="mt-4 flex justify-end space-x-2 p-4 border-t bg-muted/20 dark:bg-muted/10">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t("common.actions.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoadingLinks || isProcessing}
          >
            {isProcessing ? (
              <>
                <Spinner className="mr-2 h-4 w-4" />
                {t("common.saving")}
              </>
            ) : (
              t("common.save")
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
